import { ethers } from "ethers";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import ComposeNFTArtifact from "../artifacts/contracts/ComposeNFT.sol/ComposeNFT.json";
import LicenseSoulboundNFTArtifact from "../artifacts/contracts/LicenseSoulboundNFT.sol/LicenseSoulboundNFT.json";
import RecordProgressBar from "../components/ProgressBar/RecordProgressBar";
import MasterCopy from "../common/models/MasterCopy";
import axios, { AxiosResponse } from 'axios';
import Row from '../components/Row/Row';
import Column from '../components/Column/Column';
import Text from '../components/Text/Text';
import error_record from "../imgs/error_record.svg";
import SimpleButton from '../components/Buttons/SimpleButton';
import minting_progress_bg from "../nftengine/imgs/minting_progress_page_bg.png";
import { getSelectedProvider, sleep } from "../utils";
import { NavBar } from "../components/NavBar";

interface LicenseLoadingPageProps {
  theme: string;
}
interface LicenseLoadingPageState {
  selectedLicense: string;
  smallPrice: number;
  mediumPrice: number;
  largePrice: number;
  song: MasterCopy;
}

const LicenseLoadingContainer = styled.div`
  background: url(${minting_progress_bg});
  background-size: cover;
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const BigLetter = styled.h1`
  font-size: 150px;
  padding: 10px;
`;
const ActionContainer = styled.div`
  position: absolute;
  bottom: 25%;
  background: #9D7CFF;
  padding: 1%;
  opacity: 0.9;
  border-radius: 16px;
  margin-top: 5%;
`;

const ErrorContainer = styled(Column)`
  position: absolute;
  bottom: 20%;
  background: #932b95;
  opacity: 0.9;
  border-radius: 16px;
  padding-block: 2%;
  padding-inline: 5%;
  margin-top: 5%;
`;


export const LicenseLoadingPage: React.FC<LicenseLoadingPageProps> = () => {
  const { state } = useLocation();
  const locationState = state as LicenseLoadingPageState;
  const [loading, setLoading] = useState(true);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState(false);
  const [showAction, setShowAction] = useState(false);
  const navigate = useNavigate();

  const deployLicenseContractAndExtras = async (contract, provider) => {
    setProgressText('Deploying license contract!')
    setShowAction(true);
    let resContract = null;
    try {
      resContract = await deployContract(contract);
    }
    catch (error) {
      if (error.code === "INSUFFICIENT_FUNDS") {
        setProgressText("Insufficient funds to deploy contract");
      } else if (error.code === "ACTION_REJECTED") {
        setProgressText("Rejected transaction, please try again");
      } else {
        setProgressText("Error deploying contract, please try again");
      }
      setError(true);
      return;
    }
    setProgressText("Thanks! Continuing to deploy smart contract");
    setShowAction(false);
    await waitForTxToComplete(resContract.deployTransaction, provider);
    setProgressText("Allowing master copy to transfer license contract when sold");
    setShowAction(true);
    let approvalToTransferTx = null;
    try {
      approvalToTransferTx = await setApprovalToTransferOnSale(resContract);
    }
    catch (error) {
      if (error.code === "INSUFFICIENT_FUNDS") {
        setProgressText("Insufficient funds to deploy contract");
      } else if (error.code === "ACTION_REJECTED") {
        setProgressText("Rejected transaction, please try again");
      } else {
        setProgressText("Error allowing master copy to transfer license contract when sold, please try again");
      }
      setError(true);
      return;
    }
    setProgressText("Thanks! Continuing to allow license contract to be transferred when master copy is sold");
    setShowAction(false);
    await waitForTxToComplete(approvalToTransferTx, provider);
    setProgressText("Connecting license and master copy contracts");
    setShowAction(true);
    let tx = null;
    try {
      tx = await setLicenseContractToNFT(resContract, provider);
    } catch (error) {
      if (error.code === "INSUFFICIENT_FUNDS") {
        setProgressText("Insufficient funds to deploy contract");
      } else if (error.code === "ACTION_REJECTED") {
        setProgressText("Rejected transaction, please try again");
      } else {
        setProgressText("Error connecting license and master copy contracts, please try again");
      }
      setError(true);
      return;
    }

    setProgressText("Thanks! Continuing to connect license and master copy contracts");
    setShowAction(false);
    await waitForTxToComplete(tx, provider);
    const formData = new FormData();
    formData.append("song_id", locationState.song.id.toString());
    formData.append("license_address", resContract.address);
    formData.append("owner", await provider.getSigner().getAddress());
    axios
    .post(process.env.REACT_APP_BACKEND_URL + "/update_song_with_license", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response: AxiosResponse) => {
        navigate("/explore", { state: { showLicenseSuccessModal: true } });
    });
  };

  const waitForTxToComplete = async (tx, provider) => {
    while (true) {
      await sleep(1000);
      const currentTransaction = await provider.getTransaction(tx.hash);
      if (currentTransaction === null || currentTransaction.blockNumber !== null) {
        // Transaction has been deployed when there is a block number associated with it
        return;
      }
    }
  };

  const setLicenseContractToNFT = async (resContract, provider) => {
    const composeNFTContract = new ethers.Contract(
      locationState.song.nft_address,
      ComposeNFTArtifact.abi,
      provider.getSigner()
    );
    // TODO: Add Typechain to support types
    return (composeNFTContract as any).setLicense(
      resContract.address
    );
  };

  const deployContract = async (contract) => {
    const deployedContract = await contract.deploy(
      locationState.song.song_name + ' License ',
      locationState.song.song_name.substring(0,3),
      process.env.REACT_APP_COMPOSENFT_BANK_ADDRESS,
      process.env.REACT_APP_COMPOSENFT_SERVICER,
      process.env.REACT_APP_LICENSE_FEE,
      ethers.utils.parseEther(locationState.smallPrice.toString()),
      ethers.utils.parseEther(locationState.mediumPrice.toString()),
      ethers.utils.parseEther(locationState.largePrice.toString())
    );
    return deployedContract;
  };

  const setApprovalToTransferOnSale = async (resContract) => {
    return resContract.approveForTransfer(locationState.song.nft_address);
  };

  useEffect(() => {
    if (window.ethereum) {
     const selectedProvider = getSelectedProvider();
      const provider = new ethers.providers.Web3Provider(selectedProvider);
      const contract = new ethers.ContractFactory(
        LicenseSoulboundNFTArtifact.abi,
        LicenseSoulboundNFTArtifact.bytecode,
        provider.getSigner()
      );
      deployLicenseContractAndExtras(contract, provider);
    }
  }, []);

  const onRetryClick = () => {
    setError(false);
    if (window.ethereum) {
     const selectedProvider = getSelectedProvider();
      const provider = new ethers.providers.Web3Provider(selectedProvider);
      const contract = new ethers.ContractFactory(
        LicenseSoulboundNFTArtifact.abi,
        LicenseSoulboundNFTArtifact.bytecode,
        provider.getSigner()
      );
      deployLicenseContractAndExtras(contract, provider);
    }
  };

  return (
    <>
    <NavBar theme={'dark'} hideNavLinks={true} themeToggler={() => {}}/>
    <LicenseLoadingContainer>
      {error ? (
          <>
            <Row style={{ maxHeight: "15vh" }}>
              <BigLetter>L</BigLetter>
              <img src={error_record} style={{ width: "200px" }} />

              <BigLetter>A</BigLetter>
              <BigLetter>D</BigLetter>
              <BigLetter>I</BigLetter>
              <BigLetter>N</BigLetter>
              <BigLetter>G</BigLetter>
            </Row>
            <ErrorContainer>
              <Text>{progressText}</Text>
              <SimpleButton onClick={onRetryClick} style={{maxWidth: '50%', marginLeft: 'auto', marginRight: 'auto'}}>Retry</SimpleButton>
            </ErrorContainer>
          </>
        ) : (
          <>
            <Row style={{ maxHeight: "15vh" }}>
              <BigLetter>L</BigLetter>
              <RecordProgressBar loading={loading} strokeWidth={4} />
              <BigLetter>A</BigLetter>
              <BigLetter>D</BigLetter>
              <BigLetter>I</BigLetter>
              <BigLetter>N</BigLetter>
              <BigLetter>G</BigLetter>
            </Row>

            <Text>{progressText}</Text>
            {showAction && (
              <ActionContainer>
                Please sign the transaction in wallet to continue
              </ActionContainer>
            )}
          </>
        )}

    </LicenseLoadingContainer>
    </>
  );
};
